import { DataGridProps, DataGridRef, RowsChangeData } from '../types';
import { isRangeSelected } from '../utils/selectRange';
import { cloneDeep } from 'lodash';
import { SELECT_COLUMN_KEY } from '../Columns';
import { isCellEditable, generateUniqKey } from '../utils';

type Dictionary = Record<string, unknown>;
type CSVArray = string[][];

interface UsePasteArgs<R> {
  gridContext: DataGridRef<R>;
  patchRows: (data: RowsChangeData<R>) => void;
  pasteFormatter: DataGridProps<R>['pasteFormatter'];
}

export function usePaste<R>(options: UsePasteArgs<R>) {
  /**
   * 1. parse csv 为二维数组 csvArray [[column1-value1, column2-value1], [[column1-value2, column2-value2]]]
   * 2. csvArray 有三种情况：整列 整行 选区（单个单元格为选区的特例）
   * 3. csvArray 按行读取并 shift 出来，修改后 push 到 updatedRows
   * 4. 如 csvArray 仍有多余行，按行读取并 shift 出来，new Row 并推入到 insertedRows
   * 5. updatedRows insertedRows 触发 onRowsChange 事件
   */
  const { gridContext, patchRows, pasteFormatter } = options;
  return (text: string) => {
    const {
      rows,
      columns,
      rowKeyName,
      rowKeyGetter,
      selectedRows,
      selectedColumns,
      selectedRange,
    } = gridContext;
    const columnLength = columns.length - 1;
    const contentStartColIdx = columns.findIndex((col) => col.key !== SELECT_COLUMN_KEY);
    const csvArray = parseCSV(text);
    const updatedRows: R[] = [];
    const insertedRows: R[] = [];

    function produceRow(row: R, start: number, end: number, csvRow: string[]) {
      const draftRow = cloneDeep(row) as Dictionary;
      // 更新列值时包含 end
      while (start <= end && csvRow.length) {
        const colValue = csvRow.shift();
        const column = columns[start];
        if (isCellEditable({ row, column })) {
          draftRow[column.key] = pasteFormatter ? pasteFormatter(row, column, colValue) : colValue;
        }
        start++;
      }
      return draftRow;
    }

    function insertRows(csvArray: CSVArray, rowProducer: (csvRow: string[]) => R) {
      if (!csvArray.length) {
        return;
      }
      csvArray.forEach((csvRow) => {
        insertedRows.push(rowProducer(csvRow));
      });
    }
    if (selectedRows?.size) {
      const rowBeginIdx = Math.min(
        ...rows.map((row, idx) => {
          return selectedRows.has(rowKeyGetter(row as Dictionary)) ? idx : Number.MAX_SAFE_INTEGER;
        }),
      );
      let i = rowBeginIdx;
      for (; i < rows.length; i++) {
        const csvRow = csvArray.shift();
        if (!csvRow) {
          break;
        }
        const editedRow = produceRow(rows[i], contentStartColIdx, columnLength, csvRow);
        updatedRows.push(editedRow as R);
      }
      if (i === rows.length && csvArray.length) {
        /**
         * 剩余数据新建行
         */
        insertRows(
          csvArray,
          (csvRow: string[]) => produceRow({} as R, contentStartColIdx, columnLength, csvRow) as R,
        );
      }
    } else if (selectedColumns.size) {
      const columnBeginIdx = columns.findIndex((column) => {
        return selectedColumns.has(column.key);
      });
      csvArray.forEach((csvRow, rowIdx) => {
        if (!rows[rowIdx]) {
          return;
        }
        if (columnBeginIdx !== -1) {
          updatedRows.push(
            produceRow(rows[rowIdx], columnBeginIdx, columns.length - 1, csvRow) as R,
          );
        }
      });
    } else if (isRangeSelected(selectedRange)) {
      const startIdx = Math.min(selectedRange.columnIdx, selectedRange.endColumnIdx);
      let endIdx = Math.max(selectedRange.columnIdx, selectedRange.endColumnIdx);
      let startRowIdx = Math.min(selectedRange.rowIdx, selectedRange.endRowIdx);
      let endRowIdx = Math.max(selectedRange.rowIdx, selectedRange.endRowIdx);
      if (startIdx === endIdx && startRowIdx === endRowIdx) {
        /**
         * 单选的情况，需要自动扩展
         */
        endRowIdx = rows.length - 1;
        endIdx = columns.length - 1;
      }
      while (startRowIdx <= endRowIdx) {
        const draftRow = cloneDeep(rows[startRowIdx]) as Dictionary;
        const csvRow = csvArray.shift();
        if (csvRow) {
          const editedRow = produceRow(draftRow as R, startIdx, endIdx, csvRow);
          updatedRows.push(editedRow as R);
        }
        startRowIdx++;
      }
      const isLastRowSelected = startRowIdx === rows.length;
      if (isLastRowSelected) {
        insertRows(
          csvArray,
          (csvRow: string[]) => produceRow({} as R, startIdx, endIdx, csvRow) as R,
        );
      }
    }
    patchRows({
      modifiedRows: updatedRows,
      newRows: insertedRows?.map((item) => ({
        ...item,
        _created: true,
        [rowKeyName]: generateUniqKey(),
      })),
    });
  };
}

const newLine = '\n';
const delimiter = '\t';

function parseCSV(text: string): CSVArray {
  const csvArray: CSVArray = [];
  text = text.replace(/\r\n/g, '\n');
  if (text.endsWith('\n')) {
    text = text.substring(0, text.length - 1);
  }
  const lines = text.split(newLine);
  lines.forEach((line) => {
    const row = line.split(delimiter);
    csvArray.push(row);
  });
  return csvArray;
}
